import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import SP_API from 'components/API';
import config from 'config.json';

// Vision UI Dashboard PRO React example components
import Header from 'examples/Header';
import Footer from 'examples/Footer';
import driverImage from 'assets/images/tracking/driverTruck.png';
import sarathiLogo from 'assets/images/tracking/sarathihighresolution.png';
import vahanImage from 'assets/images/tracking/vahanHighresolution.png';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { LockOpen, Lock } from '@mui/icons-material';
import fastagLogo from 'assets/images/tracking/fastagLogoForButton.png';
import styles from './styles.module.scss';
import DriverDetails from './DriverDetails';
import moment from 'moment';
import DriverDobPicker from './driverDobPicker';

// Vision UI Dashboard PRO React context and components
import { useVisionUIController, setOpenConfigurator } from 'context';
import Configurator from 'components/Configurator';
import VuiBox from 'components/VuiBox';

import TickIcon from 'examples/Icons/TickIcon';

const perks = [
  '100% Authentic & Government-Verified Data',
  'Enhanced Security',
  'Verify Insurance Compliance',
  'Improve In Plant TAT',
  'Fast And Easy Verification',
  'Fraud Prevention',
];

function VerifyDrivers() {
  const history = useHistory();
  const [controller, dispatch] = useVisionUIController();
  const { openConfigurator } = controller;
  const [licenseNumber, setLicenseNumber] = useState('');
  const [driverDateOfBirth, setDriverDateOfBirth] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [driverData, setDriverData] = useState(null);
  const [showDriverDetails, setShowDriverDetails] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userDetails, setUserDetails] = useState({});

  const validateLicenseNumber = (number) => {
    const checkForAlphaNumeric = (vNo) => {
      let letter = /[a-zA-Z]/;
      let number = /\d/;
      let valid = number.test(vNo) && letter.test(vNo); //match a letter _and_ a number
      return valid;
    };
    return checkForAlphaNumeric(number);
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  const checkAuthentication = () => {
    const token = localStorage.getItem('token');
    return token !== null && token !== '';
  };

  // Check authentication status on component mount
  React.useEffect(() => {
    const isAuthenticated = checkAuthentication();
    setLoggedIn(isAuthenticated);

    if (isAuthenticated) {
      // Get user details from localStorage
      const storedUserDetails = localStorage.getItem('userDetails');
      if (storedUserDetails) {
        try {
          const userDetails = JSON.parse(storedUserDetails);
          setUserDetails(userDetails);
        } catch (e) {
          console.error('Error parsing user details:', e);
          setUserDetails({ name: 'User' });
        }
      } else {
        setUserDetails({ name: 'User' });
      }
    }
  }, []);

  const handleVerify = async () => {
    // Check authentication first
    if (!checkAuthentication()) {
      // Show login form
      handleConfiguratorOpen();
      return;
    }

    // Clear previous errors and data
    setError('');
    setShowDriverDetails(false);
    setDriverData(null);

    // Validate license number
    if (!licenseNumber.trim()) {
      setError('Please enter a license number');
      return;
    }

    if (!validateLicenseNumber(licenseNumber.trim())) {
      setError('Please enter a valid license number');
      return;
    }

    // Validate driver date of birth
    if (!driverDateOfBirth) {
      setError('Please enter driver date of birth');
      return;
    }

    // Check if driver is at least 18 years old
    const today = new Date();
    const dob = new Date(driverDateOfBirth);
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (
      age < 18 ||
      (age === 18 && monthDiff < 0) ||
      (age === 18 && monthDiff === 0 && today.getDate() < dob.getDate())
    ) {
      setError('Driver must be at least 18 years old');
      return;
    }

    setIsLoading(true);

    try {
      const response = await SP_API({
        method: 'POST',
        url: config.SARATHI_API,
        data: {
          drivingLicense: licenseNumber.trim(),
          dateOfBirth: driverDateOfBirth.trim(),
        },
      });

      if (response && response.data && response.data?.expiryDate) {
        setDriverData(response.data);
        setShowDriverDetails(true);
      } else {
        setError('Driver not found or invalid license number');
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error?.status === 400) {
        setError(
          error?.data || 'Failed to fetch driver details. Please try again.',
        );
        return;
      }
      setError('Failed to fetch driver details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDriverDetails = () => {
    setShowDriverDetails(false);
    setDriverData(null);
    setLicenseNumber('');
    setDriverDateOfBirth('');
  };

  const handleVerifyTruck = () => {
    history.push('/verify-vahan');
  };

  const handleFastagClick = () => {
    history.push('/track-fastag');
  };

  let licenseValidity = null;
  if (driverData && driverData.expiryDate) {
    licenseValidity = moment(driverData.licenseValidity).isAfter(moment())
      ? 'Expired'
      : 'Valid';
  }

  const licenses = driverData?.licenses;
  const vehicleClasses = licenses
    ?.map((license) => license.vehicleType)
    .join(', ');

  // Floating lock button (same as homepage)
  const configsButton = (
    <VuiBox
      id="floating_lock_button"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="5rem"
      zIndex={99}
      color="white"
      sx={({ palette: { info, success } }) => ({
        cursor: 'pointer',
        backgroundColor: loggedIn ? success.main : info.main,
      })}
      onClick={handleConfiguratorOpen}
    >
      {loggedIn ? (
        <Lock id="floating_lock_button" fontSize="default" color="inherit">
          {userDetails.name ? userDetails.name.split(' ')[0] : 'User'}
        </Lock>
      ) : (
        <LockOpen id="floating_lock_button" fontSize="default" color="inherit">
          Login
        </LockOpen>
      )}
    </VuiBox>
  );

  return (
    <>
      <Header />

      <main className={styles.heroSection}>
        {!showDriverDetails && (
          <div className={styles.heroContent}>
            <div className={styles.truckIllustration}>
              <img
                src={driverImage}
                alt="Driver"
                className={styles.truckImage}
              />
            </div>

            <div className={styles.vahanTextContainer}>
              <div className={styles.vahanLogoContainer}>
                <h1 className={styles.mainHeadline}>
                  Verify Drivers with Sarathi!
                </h1>
                <img
                  src={sarathiLogo}
                  alt="Sarathi Logo"
                  className={styles.vahanLogoImage}
                />
              </div>

              <p className={styles.subtitle}>
                Verified Drivers. Safer Cargo. Smarter Compliance.
              </p>
            </div>
          </div>
        )}
        {!showDriverDetails ? (
          <div className={styles.inputSectionContainer}>
            <form className={styles.inputElementsContainer}>
              <input
                type="text"
                placeholder="Enter Driver License No., eg DL1234567890123"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                className={styles.vehicleInput}
              />

              <DriverDobPicker
                className={styles.dobPicker}
                date={driverDateOfBirth}
                width="100%"
                setDate={setDriverDateOfBirth}
              />

              <button
                onClick={handleVerify}
                disabled={isLoading}
                className={styles.verifyButton}
              >
                {isLoading ? 'Verifying...' : 'Get Details'}
              </button>

              {showDriverDetails && (
                <div
                  className={styles.verifyDriverButton}
                  onClick={handleVerifyTruck}
                >
                  <img
                    src={vahanImage}
                    alt="Truck"
                    className={styles.vahanImage}
                  />
                  <span className={styles.sarathiText}>Verify Vehicle</span>
                </div>
              )}
            </form>
          </div>
        ) : (
          <div className={styles.inputSectionContainerVerified}>
            <div className={styles.verifiedInputSection}>
              <form className={styles.inputsRow}>
                <div
                  className={styles.backButtonResponsive}
                  onClick={handleCloseDriverDetails}
                >
                  <ChevronLeftIcon fontSize="medium" />
                  <span className={styles.backText}>Back</span>
                </div>

                <input
                  type="text"
                  placeholder="Enter Driver License No., eg DL1234567890123"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  className={styles.vehicleInputResponsive}
                />

                <DriverDobPicker
                  className={styles.dobPickerResponsive}
                  date={driverDateOfBirth}
                  setDate={setDriverDateOfBirth}
                />

                <button
                  onClick={handleVerify}
                  disabled={isLoading}
                  className={styles.verifyButtonResponsive}
                >
                  {isLoading ? (
                    'Verifying...'
                  ) : (
                    <>
                      Get<span className={styles.detailsText}> Details</span>
                    </>
                  )}
                </button>

                {/* Vahan and Fastag buttons - hidden on mobile, visible on tablet+ */}
                <div className={styles.extraButtonsResponsive}>
                  <div
                    className={styles.verifyDriverButtonResponsive}
                    onClick={handleVerifyTruck}
                  >
                    <img
                      src={vahanImage}
                      alt="Vehicle"
                      className={styles.vahanImageResponsive}
                    />
                    <span className={styles.buttonTextResponsive}>
                      Verify Vehicle
                    </span>
                  </div>

                  <div
                    className={styles.verifyDriverButtonResponsive}
                    onClick={handleFastagClick}
                  >
                    <img
                      src={fastagLogo}
                      alt="Fastag"
                      className={styles.fastagLogoResponsive}
                    />
                    <span className={styles.buttonTextResponsive}>Fastag</span>
                  </div>
                </div>
              </form>

              {/* Mobile-only second row for Vahan and Fastag */}
              <div className={styles.mobileButtonsRow}>
                <div
                  className={styles.verifyDriverButtonMobile}
                  onClick={handleVerifyTruck}
                >
                  <img
                    src={vahanImage}
                    alt="Vehicle"
                    className={styles.vahanImageMobile}
                  />
                  <span className={styles.buttonTextMobile}>
                    Verify Vehicle
                  </span>
                </div>

                <div
                  className={styles.verifyDriverButtonMobile}
                  onClick={handleFastagClick}
                >
                  <img
                    src={fastagLogo}
                    alt="Fastag"
                    className={styles.fastagLogoMobile}
                  />
                  <span className={styles.buttonTextMobile}>Fastag</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && <div className={styles.errorMessage}>{error}</div>}

        {showDriverDetails && (
          <div className={styles.vehicleDetailsCard}>
            <div className={styles.vehicleHeader}>
              <div>
                <div className={styles.vehicleNumberSection}>
                  <h2 className={styles.vehicleNumber}>
                    {driverData?.drivingLicense || ''}
                  </h2>
                  <div className={styles.vahanBrand}>
                    <img
                      src={sarathiLogo}
                      alt="Sarathi Logo"
                      className={styles.vahanLogoImage}
                    />
                  </div>
                </div>

                <div
                  className={styles.dtFastagButton}
                  onClick={handleFastagClick}
                >
                  <img
                    src={fastagLogo}
                    alt="Fastag Logo"
                    className={styles.fastagLogo}
                  />
                  <span className={styles.buttonText}>Fastag</span>
                </div>
              </div>

              <div className={styles.vehicleStatusPanels}>
                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>Current Status</span>
                  <span
                    className={`${styles.statusValue} ${
                      licenseValidity === 'Valid'
                        ? styles.rcActive
                        : styles.rcInactive
                    }`}
                  >
                    {licenseValidity}
                  </span>
                </div>

                {/* <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>Blacklist Status</span>
                  <span className={styles.statusValue}>-</span>
                </div> */}

                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>
                    Categories of Vehicle
                  </span>
                  <span className={styles.statusValue}>
                    {vehicleClasses || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <section className={styles.bottomSection}>
        {!showDriverDetails && (
          <div className={styles.perks}>
            <div className={styles.bottomHeading}>
              Get official driver license-related information in India 
            </div>
            <div className={styles.perksList}>
              {perks.map((perk, index) => (
                <div key={index} className={styles.perksListItem}>
                  <TickIcon
                    className={styles.tickIcon}
                    width={20}
                    height={20}
                    fill="#A2B2C4"
                  />
                  <p className={styles.perksListItemText}>{perk}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {showDriverDetails && (
          <DriverDetails
            driverData={driverData}
            onClose={handleCloseDriverDetails}
          />
        )}
        {/* <img src={clients} alt="Our Clients" className={styles.clientsImage} /> */}
      </section>
      <Footer />
      <Configurator
        userDetails={loggedIn ? userDetails : null}
        isShowConfig={!openConfigurator}
      />
      {configsButton}
    </>
  );
}

export default VerifyDrivers;
